import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type { ProposedEvidenceItem } from '@/lib/types';

const BASE = '/api/v1/compliance/proposed-evidence';
const FIVE_MINUTES = 1000 * 60 * 5;

export function useProposedEvidence() {
  return useQuery({
    queryKey: queryKeys.compliance.proposedEvidence(),
    queryFn: async (): Promise<ProposedEvidenceItem[]> => {
      const response = await apiClient.get<ProposedEvidenceItem[]>(`${BASE}/`);
      return response.data;
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useBatchConfirmEvidence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: number[]): Promise<{ confirmed: number[]; count: number }> => {
      const response = await apiClient.post<{ confirmed: number[]; count: number }>(
        `${BASE}/batch-confirm/`,
        { ids },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.compliance.proposedEvidence() });
      queryClient.invalidateQueries({ queryKey: queryKeys.clauseStatus.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.compliance.dashboard() });
      queryClient.invalidateQueries({ queryKey: queryKeys.compliance.summary() });
    },
  });
}

export function useBatchDismissEvidence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: number[]): Promise<{ dismissed: number }> => {
      const response = await apiClient.post<{ dismissed: number }>(
        `${BASE}/batch-dismiss/`,
        { ids },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.compliance.proposedEvidence() });
      queryClient.invalidateQueries({ queryKey: queryKeys.clauseStatus.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.compliance.dashboard() });
      queryClient.invalidateQueries({ queryKey: queryKeys.compliance.summary() });
    },
  });
}

export interface EvidenceScanResult {
  scanned: number;
  proposed: number;
  already_linked: number;
}

export function useScanEvidence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<EvidenceScanResult> => {
      const response = await apiClient.post<EvidenceScanResult>(`${BASE}/scan/`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.compliance.proposedEvidence() });
      queryClient.invalidateQueries({ queryKey: queryKeys.compliance.summary() });
      queryClient.invalidateQueries({ queryKey: queryKeys.compliance.dashboard() });
      queryClient.invalidateQueries({ queryKey: queryKeys.clauseStatus.all });
    },
  });
}
